import { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue, Fragment } from 'react';
import JSZip from 'jszip';
import { List, useListRef } from 'react-window';
import type { RowComponentProps } from 'react-window';
import {
  Box, Typography, Dialog, DialogContent, IconButton, TextField, InputAdornment,
  CircularProgress, ToggleButton, ToggleButtonGroup, Tooltip, Alert,
  Menu, MenuItem, Button, Popover,
} from '@mui/material';
import {
  Close as CloseIcon,
  Clear as ClearIcon,
  Circle as CircleIcon,
  DragHandle as DragHandleIcon,
  ArrowDropDown as DropDownIcon,
  KeyboardArrowUp as PrevIcon,
  KeyboardArrowDown as NextIcon,
  HelpOutline as HelpIcon,
  IosShare as ShareIcon,
} from '@mui/icons-material';
import { api } from '@/lib/api';
import { colors } from '@/theme/tokens';
import { LogShareDialog } from './log-share-dialog';
import { BETA_FEATURE_HINT } from '@/components/ui/demo-feature';
import {
  LOG_SHARE_CONTEXT,
  MAX_LOG_SHARE_LINES,
  type LogShareFile,
  type LogShareLine,
} from '@/lib/log-share.service';
import type { ViewerFile } from './multi-file-log-viewer';

export const KLOGG_MAX_FILES = 10;
export const KLOGG_MAX_TOTAL_BYTES = 10 * 1024 * 1024;

const FILE_COLORS = ['#4FC3F7', '#FF8A65', '#81C784', '#CE93D8', '#FFD54F', '#F48FB1', '#80DEEA', '#A5D6A7', '#FFAB91', '#9FA8DA'];
const PREDEFINED_FILTERS = ['ERROR', 'WARN', 'FATAL', 'INFO', 'DEBUG', 'panic', 'TIMEOUT'];
const SHORTCUTS: ReadonlyArray<{ keys: string; description: string }> = [
  { keys: 'Ctrl/⌘ + F', description: 'Focus filter input' },
  { keys: 'Enter', description: 'Next match (while filter input is focused)' },
  { keys: 'Shift + Enter', description: 'Previous match (while filter input is focused)' },
  { keys: 'n / F3', description: 'Next match' },
  { keys: 'Shift + N / Shift + F3', description: 'Previous match' },
  { keys: 'Esc', description: 'Clear filter / close help / close viewer' },
  { keys: '?', description: 'Toggle this shortcuts panel' },
];
const ROW_HEIGHT = 22;
const EXPANDED_LINE_HEIGHT = 16;
const EXPANDED_VPAD = 10;
const EXPANDED_MAX = 400;
const CHAR_WIDTH = 6.6;
const GUTTER_BASE = 48 + 22 + 8 + 8; // # + M + padding L/R
const FILE_COL_WIDTH = 100;
const MIN_BOT_HEIGHT = 80;
const MAX_BOT_HEIGHT_RATIO = 0.7;
const DEFAULT_BOT_HEIGHT = 320;
// Anchored to start of line so we never mistake an embedded date (e.g. a
// build-date inside parens like "CLM: 9.9.8_SS (2021-05-10 15:34:43)") for the
// actual log time. Two flavours observed:
//   [2026-05-20 04:43:46.708] ...     — bracketed wall-clock
//   05-21 04:32:24.384 485 603 I ...  — Android logcat (no year)
const TS_BRACKETED_RE = /^\[(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2}(?:\.\d+)?)\]/;
const TS_ANDROID_RE = /^(\d{2})-(\d{2})\s+(\d{2}:\d{2}:\d{2}(?:\.\d+)?)/;
// Pulled from "log_20260521_03…" — gives Android logcat lines a year so they
// can merge alongside the bracketed `[YYYY-MM-DD …]` lines.
const FILE_YEAR_RE = /(20\d{2})\d{4}/;

interface ParsedLine {
  lineNumber: number;
  text: string;
  timestamp?: Date;
  fileIndex: number;
  fileName: string;
  innerFileName: string;
}

interface FileData {
  file: ViewerFile;
  color: string;
  lines: ParsedLine[];
  loading: boolean;
  bytes: number;
  error?: string;
}

interface ViewState {
  filterText: string;
  caseSensitive: boolean;
  isRegex: boolean;
  wholeWord: boolean;
  marks: Set<number>;
  selectedIndex: number | null;
  botSelectedIndex: number | null;
  expandedTop: Set<number>;
  expandedBot: Set<number>;
}

const EMPTY_VIEW: ViewState = {
  filterText: '', caseSensitive: false, isRegex: false, wholeWord: false,
  marks: new Set(), selectedIndex: null, botSelectedIndex: null,
  expandedTop: new Set(), expandedBot: new Set(),
};

function extractFileBaseYear(filename: string): number | undefined {
  const m = FILE_YEAR_RE.exec(filename);
  if (!m) return undefined;
  const year = parseInt(m[1], 10);
  return year >= 2000 && year <= 2100 ? year : undefined;
}

function parseTimestamp(line: string, fileBaseYear?: number): Date | undefined {
  const mb = TS_BRACKETED_RE.exec(line);
  if (mb) {
    const d = new Date(`${mb[1]}T${mb[2]}Z`);
    return isNaN(d.getTime()) ? undefined : d;
  }

  const ma = TS_ANDROID_RE.exec(line);
  if (ma) {
    const year = fileBaseYear ?? new Date().getFullYear();
    const d = new Date(`${year}-${ma[1]}-${ma[2]}T${ma[3]}Z`);
    return isNaN(d.getTime()) ? undefined : d;
  }

  return undefined;
}

function parseFileContent(content: string, fileIndex: number, fileName: string, innerFileName: string): ParsedLine[] {
  const baseYear = extractFileBaseYear(innerFileName) ?? extractFileBaseYear(fileName);
  const raw = content.split('\n');
  const out: ParsedLine[] = [];
  for (let i = 0; i < raw.length; i++) {
    if (raw[i]) {
      out.push({
        lineNumber: i + 1,
        text: raw[i],
        timestamp: parseTimestamp(raw[i], baseYear),
        fileIndex,
        fileName,
        innerFileName,
      });
    }
  }
  return out;
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildFilterRegex(
  pattern: string,
  opts: { caseSensitive: boolean; isRegex: boolean; wholeWord: boolean },
): RegExp | null {
  if (!pattern) return null;
  let src = opts.isRegex ? pattern : escapeRegex(pattern);
  if (opts.wholeWord) src = `\\b${src}\\b`;
  try {
    return new RegExp(src, opts.caseSensitive ? 'g' : 'gi');
  } catch {
    return null;
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MiB`;
}

function computeExpandedHeight(text: string, showFile: boolean, containerWidth: number): number {
  const gutter = GUTTER_BASE + (showFile ? FILE_COL_WIDTH : 0);
  const usable = Math.max(120, containerWidth - gutter);
  const charsPerLine = Math.max(20, Math.floor(usable / CHAR_WIDTH));
  const lines = Math.max(1, Math.ceil(text.length / charsPerLine));
  return Math.max(ROW_HEIGHT, Math.min(EXPANDED_MAX, lines * EXPANDED_LINE_HEIGHT + EXPANDED_VPAD));
}

function highlightText(text: string, regex: RegExp | null): React.ReactNode {
  if (!regex) return text;
  const parts = text.split(regex);
  if (parts.length <= 1) return text;
  const matches = text.match(regex);
  if (!matches) return text;
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && matches[i] && (
        <mark style={{ background: '#FEF3A3', color: '#171717', borderRadius: 2, padding: '0 2px' }}>
          {matches[i]}
        </mark>
      )}
    </span>
  ));
}

function ColumnHeader({ showFile }: { showFile: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', height: 24, px: 0.5,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#F9FAFB',
        fontFamily: 'monospace', fontSize: 10, fontWeight: 600, color: 'text.secondary',
        userSelect: 'none', flexShrink: 0,
      }}
    >
      <Box sx={{ width: 48, textAlign: 'right', pr: 1, flexShrink: 0 }}>#</Box>
      {/* The gesture is invisible otherwise: nothing about a column of dots
          suggests you can drag down it or shift-click across it. */}
      <Tooltip title="Mark lines: click, drag down, or shift-click for a range">
        <Box sx={{ width: 22, textAlign: 'center', flexShrink: 0, cursor: 'help' }}>M</Box>
      </Tooltip>
      {showFile && <Box sx={{ width: 100, flexShrink: 0 }}>FILE</Box>}
      <Box sx={{ flex: 1, minWidth: 0 }}>CONTENT</Box>
    </Box>
  );
}

interface LineRowProps {
  lines: ParsedLine[];
  selectedIndex: number | null;
  marks: Set<number>;
  expanded: Set<number>;
  fileColors: Map<number, string>;
  showFile: boolean;
  onSelect: (index: number) => void;
  onToggleMark: ((index: number) => void) | null;
  // Drag-to-paint across the gutter. Separate from onToggleMark because a drag
  // sets every line it crosses to the SAME state (whatever the first line
  // flipped to), where repeated toggling would alternate and leave gaps.
  // `shift` requests a range from the last marked line instead of a drag.
  onGutterDown?: (index: number, shift: boolean) => void;
  onGutterEnter?: (index: number) => void;
  filterRegex: RegExp | null;
  // Returns the number displayed in the # column. Lets merge-mode show the
  // merged sequence (1..N) instead of each line's per-file original number,
  // which would otherwise jump around (9427 → 14550 → 1 → 2 …) when lines
  // are sorted by timestamp across files.
  getDisplayNumber: (index: number, line: ParsedLine) => number;
}

function LineRowRenderer(props: RowComponentProps<LineRowProps>) {
  const { index, style, lines, selectedIndex, marks, expanded, fileColors, showFile, onSelect, onToggleMark, onGutterDown, onGutterEnter, filterRegex, getDisplayNumber } = props;
  const line = lines[index];
  const isSelected = selectedIndex === index;
  const isMarked = marks.has(index);
  const isExpanded = expanded.has(index);
  const fileColor = fileColors.get(line.fileIndex) ?? colors.text.tertiary;

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: isExpanded ? 'flex-start' : 'center',
        paddingLeft: 4, paddingRight: 4,
        paddingTop: isExpanded ? 4 : 0,
        cursor: 'pointer', fontFamily: 'monospace', fontSize: 11,
        lineHeight: `${isExpanded ? EXPANDED_LINE_HEIGHT : ROW_HEIGHT}px`,
        backgroundColor: isSelected ? 'rgba(33,150,243,0.16)' : (isExpanded ? 'rgba(33,150,243,0.06)' : undefined),
        overflow: 'hidden',
        borderBottom: isExpanded ? '1px solid' : undefined,
        borderColor: isExpanded ? '#E5E7EB' : undefined,
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-expanded={isExpanded}
      // Drag continues on the whole ROW, not just the gutter. A 22px-wide
      // column is impossible to stay inside while dragging down a long list —
      // the pointer drifts, the paint stops, and the feature reads as broken.
      // The row is full width, so only vertical movement matters. No-ops unless
      // a drag is actually in flight.
      onMouseEnter={onGutterEnter ? () => onGutterEnter(index) : undefined}
      onClick={() => onSelect(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(index);
        }
      }}
    >
      <span style={{ width: 48, textAlign: 'right', paddingRight: 8, color: colors.text.tertiary, flexShrink: 0, fontSize: 10 }}>
        {getDisplayNumber(index, line)}
      </span>
      <span
        // When drag-painting is wired up, mousedown already toggled this line —
        // letting the click through as well would toggle it straight back and a
        // plain click would appear to do nothing. The filter pane has no drag
        // handlers, so it keeps the click.
        onClick={!onGutterDown && onToggleMark ? (e) => { e.stopPropagation(); onToggleMark(index); } : undefined}
        onMouseDown={onGutterDown ? (e) => {
          // Left button only, and swallow the event so the row's own click
          // handler does not also move the selection while painting.
          // preventDefault also stops shift-click selecting text across rows.
          if (e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          onGutterDown(index, e.shiftKey);
        } : undefined}
        style={{
          width: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, cursor: onToggleMark ? 'pointer' : 'default',
          height: ROW_HEIGHT,
        }}
      >
        {isMarked && (
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: colors.primary.main }} />
        )}
      </span>
      {showFile && (
        <span style={{ width: 100, flexShrink: 0, color: fileColor, fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {line.innerFileName}
        </span>
      )}
      <span
        style={{
          flex: 1, minWidth: 0,
          overflow: 'hidden',
          textOverflow: isExpanded ? 'unset' : 'ellipsis',
          whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
          wordBreak: isExpanded ? 'break-all' : 'normal',
          paddingRight: 8,
        }}
      >
        {highlightText(line.text, filterRegex)}
      </span>
    </div>
  );
}

function ResizeHandle({ onDrag }: { onDrag: (deltaY: number) => void }) {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Emit the INCREMENTAL delta (movement since the previous event), not the
      // cumulative offset from drag start: the consumer accumulates onto a ref
      // it overwrites every frame, so a cumulative delta compounds and the pane
      // overshoots the cursor (drag 20px → grows far more, accelerating).
      let lastY = e.clientY;
      const onMove = (ev: MouseEvent) => {
        onDrag(ev.clientY - lastY);
        lastY = ev.clientY;
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [onDrag],
  );

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        height: 6, cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: '#F4F4F4', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
        flexShrink: 0, '&:hover': { bgcolor: '#E0E0E0' },
      }}
    >
      <DragHandleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
    </Box>
  );
}

/** A line addressed the way a share stores it: by file and in-file position. */
export interface KloggMarkRef {
  file: string;
  innerFile?: string;
  lineNumber: number;
}

interface MultiFileLogKloggProps {
  open: boolean;
  onClose: () => void;
  files: ViewerFile[];
  deviceId: string;
  /**
   * Marks to restore once the files have loaded, addressed by file + in-file
   * line number. Used when reopening a shared excerpt in the full viewer: the
   * stored addresses are resolved against whatever merge this session produces,
   * so they land on the right lines even though the merged indices differ.
   */
  initialMarks?: KloggMarkRef[];
  /** Set false to hide the share affordance (e.g. when already viewing a share). */
  shareable?: boolean;
}

export function MultiFileLogKlogg({
  open, onClose, files, deviceId, initialMarks, shareable = true,
}: MultiFileLogKloggProps) {
  const tooManyFiles = files.length > KLOGG_MAX_FILES;

  const [fileDataList, setFileDataList] = useState<FileData[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState<'tabs' | 'merge'>('tabs');
  const [sizeExceeded, setSizeExceeded] = useState(false);

  const [statesByView, setStatesByView] = useState<Record<string, ViewState>>({});

  const [botHeight, setBotHeight] = useState(DEFAULT_BOT_HEIGHT);
  const botHeightRef = useRef(botHeight);
  const topPaneRef = useRef<HTMLDivElement>(null);
  const [topPaneSize, setTopPaneSize] = useState({ width: 1200, height: 400 });
  const topListRef = useListRef(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [presetsAnchor, setPresetsAnchor] = useState<HTMLElement | null>(null);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  const filterInputRef = useRef<HTMLInputElement | null>(null);
  const helpButtonRef = useRef<HTMLButtonElement | null>(null);
  const tabStripRef = useRef<HTMLDivElement | null>(null);

  const viewKey = viewMode === 'tabs' ? `tab:${activeTab}` : viewMode;
  const cur = statesByView[viewKey] ?? EMPTY_VIEW;
  const { filterText, caseSensitive, isRegex, wholeWord, marks, selectedIndex, botSelectedIndex, expandedTop, expandedBot } = cur;

  const patch = useCallback(
    (partial: Partial<ViewState> | ((prev: ViewState) => Partial<ViewState>)) => {
      setStatesByView((all) => {
        const prev = all[viewKey] ?? EMPTY_VIEW;
        const next = typeof partial === 'function' ? partial(prev) : partial;
        return { ...all, [viewKey]: { ...prev, ...next } };
      });
    },
    [viewKey],
  );

  // `open` is in the deps because MUI Dialog mounts its content only when
  // open=true. Without re-running on open, the effect's first pass sees
  // `topPaneRef.current === null` (Box not yet in the DOM) and returns early —
  // leaving topPaneSize stuck at the initial fallback and the List sized for a
  // viewport much smaller than the actual pane.
  useEffect(() => {
    if (!open) return;
    const el = topPaneRef.current;
    if (!el) return;

    // Initial sync measurement so the first paint uses real dimensions instead
    // of the (1200, 400) fallback — ResizeObserver's first fire is async.
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setTopPaneSize({ width: rect.width, height: rect.height });
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTopPaneSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, tooManyFiles]);

  // Load files
  useEffect(() => {
    if (!open || files.length === 0 || tooManyFiles) return;

    let cancelled = false;
    const initial: FileData[] = files.map((f, i) => ({
      file: f, color: FILE_COLORS[i % FILE_COLORS.length], lines: [], loading: true, bytes: 0,
    }));

    setFileDataList(initial);
    setActiveTab(0);
    setViewMode('tabs');
    setStatesByView({});
    setSizeExceeded(false);

    let totalBytes = 0;

    files.forEach((file, fileIndex) => {
      api.get(file.url, { responseType: 'arraybuffer' })
        .then(async (res) => {
          if (cancelled) return;
          const buf = res.data as ArrayBuffer;
          totalBytes += buf.byteLength;
          if (totalBytes > KLOGG_MAX_TOTAL_BYTES) {
            cancelled = true;
            setSizeExceeded(true);
            setFileDataList((prev) =>
              prev.map((fd) => fd.loading ? { ...fd, loading: false, error: 'Aborted: total size exceeded 10 MB' } : fd),
            );
            return;
          }

          if (file.isZip) {
            const zip = await JSZip.loadAsync(buf);
            const allLines: ParsedLine[] = [];
            const entries: [string, JSZip.JSZipObject][] = [];
            zip.forEach((path, entry) => { if (!entry.dir) entries.push([path, entry]); });
            entries.sort(([a], [b]) => a.localeCompare(b));
            for (const [path, entry] of entries) {
              try {
                const content = await entry.async('string');
                allLines.push(...parseFileContent(content, fileIndex, file.displayName, path));
              } catch { /* skip binary */ }
            }
            if (cancelled) return;
            setFileDataList((prev) =>
              prev.map((fd, i) => i === fileIndex ? { ...fd, lines: allLines, loading: false, bytes: buf.byteLength } : fd),
            );
          } else {
            const text = new TextDecoder('utf-8', { fatal: false }).decode(buf);
            const lines = parseFileContent(text, fileIndex, file.displayName, file.displayName);
            if (cancelled) return;
            setFileDataList((prev) =>
              prev.map((fd, i) => i === fileIndex ? { ...fd, lines, loading: false, bytes: buf.byteLength } : fd),
            );
          }
        })
        .catch(() => {
          if (cancelled) return;
          setFileDataList((prev) =>
            prev.map((fd, i) => i === fileIndex ? { ...fd, loading: false, error: 'Failed to load' } : fd),
          );
        });
    });

    return () => { cancelled = true; };
  }, [open, files, deviceId, tooManyFiles]);

  const filterRegex = useMemo(
    () => buildFilterRegex(filterText, { caseSensitive, isRegex, wholeWord }),
    [filterText, caseSensitive, isRegex, wholeWord],
  );

  const mergedLines = useMemo(() => {
    const lines = fileDataList.flatMap((fd) => fd.lines);
    lines.sort((a, b) => {
      if (a.timestamp && b.timestamp) return a.timestamp.getTime() - b.timestamp.getTime();
      if (a.timestamp) return -1;
      if (b.timestamp) return 1;
      // Both unparseable (e.g. dmesg `[ 0.829152]` uptime, "----- beginning of
      // system" markers): cluster by file, then keep file-internal order.
      if (a.fileIndex !== b.fileIndex) return a.fileIndex - b.fileIndex;
      return a.lineNumber - b.lineNumber;
    });
    return lines;
  }, [fileDataList]);

  const topLines = useMemo(
    () => (viewMode === 'merge' ? mergedLines : fileDataList[activeTab]?.lines ?? []),
    [viewMode, mergedLines, fileDataList, activeTab],
  );

  // Defer the heavy lines array so tab clicks commit the UI (tab highlight,
  // path bar, filter input) at high priority and let the List swap content
  // at low priority. Without this, startTransition would still block the
  // tab-highlight commit until the new List finished rendering.
  const deferredTopLines = useDeferredValue(topLines);
  const isContentStale = deferredTopLines !== topLines;

  const filteredIndices = useMemo(() => {
    if (!filterRegex) return [];
    const re = filterRegex;
    const out: number[] = [];
    for (let i = 0; i < deferredTopLines.length; i++) {
      re.lastIndex = 0;
      if (re.test(deferredTopLines[i].text)) out.push(i);
    }
    return out;
  }, [deferredTopLines, filterRegex]);

  const filteredLines = useMemo(
    () => filteredIndices.map((i) => deferredTopLines[i]),
    [filteredIndices, deferredTopLines],
  );

  const filteredLookup = useMemo(() => {
    const m = new Map<number, number>();
    for (let i = 0; i < filteredIndices.length; i++) m.set(filteredIndices[i], i);
    return m;
  }, [filteredIndices]);

  const fileColorMap = useMemo(() => {
    const map = new Map<number, string>();
    fileDataList.forEach((fd, i) => map.set(i, fd.color));
    return map;
  }, [fileDataList]);

  const handleTopSelect = useCallback((index: number) => {
    patch((prev) => {
      const nextExpanded = new Set(prev.expandedTop);
      if (nextExpanded.has(index)) nextExpanded.delete(index); else nextExpanded.add(index);
      return {
        selectedIndex: index,
        botSelectedIndex: filteredLookup.get(index) ?? null,
        expandedTop: nextExpanded,
      };
    });
  }, [patch, filteredLookup]);

  const handleBotSelect = useCallback((index: number) => {
    const topIdx = filteredIndices[index];
    if (topIdx === undefined) return;
    patch((prev) => {
      const nextExpanded = new Set(prev.expandedBot);
      if (nextExpanded.has(index)) nextExpanded.delete(index); else nextExpanded.add(index);
      return {
        selectedIndex: topIdx,
        botSelectedIndex: index,
        expandedBot: nextExpanded,
      };
    });
    topListRef.current?.scrollToRow({ index: topIdx, align: 'center' });
  }, [patch, filteredIndices, topListRef]);

  const handleToggleMark = useCallback((index: number) => {
    patch((prev) => {
      const next = new Set(prev.marks);
      if (next.has(index)) next.delete(index); else next.add(index);
      return { marks: next };
    });
  }, [patch]);

  // Drag-to-paint. The drag lives in a ref so crossing a row does not re-render
  // every other row; only the marks Set changing does that. `mode` is decided by
  // the first line the drag touches, so a drag across mixed lines sets them all
  // the same way instead of alternating.
  const dragRef = useRef<{ mode: 'mark' | 'unmark' } | null>(null);

  const applyPaint = useCallback((index: number, mode: 'mark' | 'unmark') => {
    patch((prev) => {
      const has = prev.marks.has(index);
      if (mode === 'mark' ? has : !has) return {};
      const next = new Set(prev.marks);
      if (mode === 'mark') next.add(index); else next.delete(index);
      return { marks: next };
    });
  }, [patch]);

  // Where the last gutter interaction landed, so shift-click has something to
  // measure a range from.
  const anchorRef = useRef<number | null>(null);

  const handleGutterDown = useCallback((index: number, shift: boolean) => {
    // Shift-click marks everything between the last touched line and this one.
    // Dragging is precise but tiring over a forty-line stack trace; clicking the
    // first line and shift-clicking the last is the forgiving way to do it, and
    // it is what people try first.
    if (shift && anchorRef.current !== null && anchorRef.current !== index) {
      const lo = Math.min(anchorRef.current, index);
      const hi = Math.max(anchorRef.current, index);
      patch((prev) => {
        const next = new Set(prev.marks);
        for (let i = lo; i <= hi; i++) next.add(i);
        return { marks: next };
      });
      anchorRef.current = index;
      return;
    }
    const mode: 'mark' | 'unmark' = marks.has(index) ? 'unmark' : 'mark';
    dragRef.current = { mode };
    anchorRef.current = index;
    applyPaint(index, mode);
  }, [marks, applyPaint, patch]);

  const handleGutterEnter = useCallback((index: number) => {
    const drag = dragRef.current;
    if (drag) applyPaint(index, drag.mode);
  }, [applyPaint]);

  // Ends the drag wherever the mouse is released, including outside the window.
  // Without this a release over browser chrome leaves the drag latched and the
  // next hover keeps painting.
  useEffect(() => {
    if (!open) return;
    const end = () => { dragRef.current = null; };
    window.addEventListener('mouseup', end);
    window.addEventListener('blur', end);
    return () => {
      window.removeEventListener('mouseup', end);
      window.removeEventListener('blur', end);
    };
  }, [open]);

  // Restore marks handed in by a shared excerpt, once per view.
  //
  // The stored addresses are (file, innerFile, lineNumber) — resolved against
  // whatever merge THIS session produces, because the merged index a line sat at
  // when it was shared says nothing about where it sits now. Once per viewKey so
  // switching tabs surfaces the marks there too, but clearing them sticks.
  const restoredViewsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!open || !initialMarks || initialMarks.length === 0) return;
    if (deferredTopLines.length === 0) return;
    if (restoredViewsRef.current.has(viewKey)) return;
    restoredViewsRef.current.add(viewKey);

    const want = new Set(
      initialMarks.map((m) => `${m.file}\u0000${m.innerFile ?? ''}\u0000${m.lineNumber}`),
    );
    const found = new Set<number>();
    deferredTopLines.forEach((line, i) => {
      if (want.has(`${line.fileName}\u0000${line.innerFileName}\u0000${line.lineNumber}`)) {
        found.add(i);
      }
    });
    if (found.size === 0) return;
    const first = Math.min(...found);
    patch({ marks: found, selectedIndex: first });
    topListRef.current?.scrollToRow({ index: first, align: 'center' });
  }, [open, initialMarks, deferredTopLines, viewKey, patch, topListRef]);

  // Marked lines plus LOG_SHARE_CONTEXT either side, addressed by file and
  // in-file line number so the excerpt survives being read in a different
  // session. Context comes from the merged view rather than per file, because in
  // merge mode the interleaving IS the information — a kernel line landing
  // between two app lines is often the whole story.
  const buildExcerpt = useCallback((): LogShareLine[] => {
    const keep = new Set<number>();
    marks.forEach((i) => {
      for (let j = i - LOG_SHARE_CONTEXT; j <= i + LOG_SHARE_CONTEXT; j++) {
        if (j >= 0 && j < deferredTopLines.length) keep.add(j);
      }
    });
    return [...keep]
      .sort((a, b) => a - b)
      .slice(0, MAX_LOG_SHARE_LINES)
      .map((i) => {
        const line = deferredTopLines[i];
        return {
          file: line.fileName,
          inner_file: line.innerFileName,
          line_number: line.lineNumber,
          text: line.text,
          marked: marks.has(i),
        };
      });
  }, [marks, deferredTopLines]);

  const shareFiles: LogShareFile[] = useMemo(
    () => files.map((f) => ({ display_name: f.displayName, url: f.url, is_zip: f.isZip })),
    [files],
  );

  const navigateMatch = useCallback((direction: 'prev' | 'next') => {
    if (filteredIndices.length === 0) return;
    const curIdx = selectedIndex ?? -1;
    let target: number;
    if (direction === 'next') target = filteredIndices.find((i) => i > curIdx) ?? filteredIndices[0];
    else target = [...filteredIndices].reverse().find((i) => i < curIdx) ?? filteredIndices[filteredIndices.length - 1];
    patch({ selectedIndex: target, botSelectedIndex: filteredLookup.get(target) ?? null });
    topListRef.current?.scrollToRow({ index: target, align: 'center' });
  }, [filteredIndices, selectedIndex, filteredLookup, patch, topListRef]);

  // Jump between bookmarked (marked) lines in the top pane — same model as
  // navigateMatch, but over the sorted set of marked top-line indices.
  const navigateMarks = useCallback((direction: 'prev' | 'next') => {
    if (marks.size === 0) return;
    const sorted = [...marks].sort((a, b) => a - b);
    const curIdx = selectedIndex ?? -1;
    let target: number;
    if (direction === 'next') target = sorted.find((i) => i > curIdx) ?? sorted[0];
    else target = [...sorted].reverse().find((i) => i < curIdx) ?? sorted[sorted.length - 1];
    patch({ selectedIndex: target, botSelectedIndex: filteredLookup.get(target) ?? null });
    topListRef.current?.scrollToRow({ index: target, align: 'center' });
  }, [marks, selectedIndex, filteredLookup, patch, topListRef]);

  const handleResize = useCallback((deltaY: number) => {
    const viewport = typeof window !== 'undefined' ? window.innerHeight : 800;
    const max = Math.max(MIN_BOT_HEIGHT, viewport * MAX_BOT_HEIGHT_RATIO);
    const next = Math.max(MIN_BOT_HEIGHT, Math.min(max, botHeightRef.current - deltaY));
    botHeightRef.current = next;
    setBotHeight(next);
  }, []);

  // Global keyboard shortcuts while the viewer is open. Dialog's own Esc-close
  // is disabled below; we handle Esc ourselves so it can fall through:
  // help → filter clear → close viewer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inFilterInput = !!target && target === filterInputRef.current;
      const inAnyInput =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        filterInputRef.current?.focus();
        filterInputRef.current?.select();
        return;
      }

      if (e.key === 'Escape') {
        if (helpAnchor) {
          e.preventDefault();
          setHelpAnchor(null);
          return;
        }
        if (inFilterInput && filterText) {
          e.preventDefault();
          patch({ filterText: '' });
          return;
        }
        e.preventDefault();
        onClose();
        return;
      }

      if (inFilterInput && e.key === 'Enter' && filteredIndices.length > 0) {
        e.preventDefault();
        navigateMatch(e.shiftKey ? 'prev' : 'next');
        return;
      }

      if (!inAnyInput && filteredIndices.length > 0 && (e.key === 'F3' || e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        navigateMatch(e.shiftKey ? 'prev' : 'next');
        return;
      }

      if (!inAnyInput && e.key === '?') {
        e.preventDefault();
        setHelpAnchor((cur) => (cur ? null : helpButtonRef.current));
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, helpAnchor, filterText, filteredIndices.length, navigateMatch, patch, onClose]);

  const isLoading = fileDataList.some((fd) => fd.loading);
  const totalBytes = useMemo(() => fileDataList.reduce((s, fd) => s + fd.bytes, 0), [fileDataList]);
  const activeFile = fileDataList[activeTab];

  // Keep the active tab reachable when the strip overflows: scroll it into view
  // on tab change / when new files load. macOS hides scrollbars, so without this
  // the tabs past the right edge are unreachable by mouse.
  useEffect(() => {
    if (viewMode !== 'tabs') return;
    const strip = tabStripRef.current;
    const el = strip?.querySelector<HTMLElement>(`[data-tab-index="${activeTab}"]`);
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab, viewMode, fileDataList.length]);

  // Variable-height row functions. react-window v2 re-measures when these functions
  // change identity, so they must be memoized on the inputs that affect height.
  const topRowHeight = useCallback(
    (index: number) => {
      if (!expandedTop.has(index)) return ROW_HEIGHT;
      const line = deferredTopLines[index];
      if (!line) return ROW_HEIGHT;
      return computeExpandedHeight(line.text, viewMode === 'merge', topPaneSize.width);
    },
    [expandedTop, deferredTopLines, viewMode, topPaneSize.width],
  );

  const botRowHeight = useCallback(
    (index: number) => {
      if (!expandedBot.has(index)) return ROW_HEIGHT;
      const line = filteredLines[index];
      if (!line) return ROW_HEIGHT;
      return computeExpandedHeight(line.text, viewMode === 'merge', topPaneSize.width);
    },
    [expandedBot, filteredLines, viewMode, topPaneSize.width],
  );

  // Auto-size the bottom pane to its content, capped by user's drag preference and screen ratio.
  const desiredBotContent = useMemo(() => {
    if (!filterRegex) return 0;
    if (filteredLines.length === 0) return 48; // placeholder copy
    let sum = 0;
    for (let i = 0; i < filteredLines.length; i++) {
      sum += botRowHeight(i);
      if (sum > botHeight) return sum;
    }
    return sum;
  }, [filterRegex, filteredLines, botRowHeight, botHeight]);

  // klTop fills remaining space via CSS flex; klBot auto-sizes to content,
  // capped by user's drag preference and a viewport-based ratio.
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const maxBotHeight = Math.max(MIN_BOT_HEIGHT, viewportHeight * MAX_BOT_HEIGHT_RATIO);
  const effectiveBotHeight = filterRegex
    ? Math.min(Math.max(48, desiredBotContent) + 24 /* col header */, botHeight, maxBotHeight)
    : 0;

  // In merge mode the per-file lineNumber jumps around when sorted by timestamp,
  // so show the merged sequence (1..N) in the # column. In tabs mode keep the
  // original file line number so the user can correlate back to the source file.
  const topGetDisplayNumber = useCallback(
    (i: number, line: ParsedLine) => (viewMode === 'merge' ? i + 1 : line.lineNumber),
    [viewMode],
  );

  const topRowProps: LineRowProps = useMemo(
    () => ({
      lines: deferredTopLines, selectedIndex, marks, expanded: expandedTop, fileColors: fileColorMap,
      showFile: viewMode === 'merge', onSelect: handleTopSelect, onToggleMark: handleToggleMark,
      onGutterDown: handleGutterDown, onGutterEnter: handleGutterEnter,
      filterRegex,
      getDisplayNumber: topGetDisplayNumber,
    }),
    [deferredTopLines, selectedIndex, marks, expandedTop, fileColorMap, viewMode, handleTopSelect, handleToggleMark, handleGutterDown, handleGutterEnter, filterRegex, topGetDisplayNumber],
  );

  // klBot displays marks at indices mapped from top's index space.
  const botMarks = useMemo(() => {
    const out = new Set<number>();
    marks.forEach((topIdx) => {
      const botIdx = filteredLookup.get(topIdx);
      if (botIdx !== undefined) out.add(botIdx);
    });
    return out;
  }, [marks, filteredLookup]);

  // For each filtered row, show the top-pane's display number (so users can
  // jump from a match in klBot back to its position in klTop). In merge mode
  // that's filteredIndices[i]+1 (merged sequence); in tabs mode it's the
  // original per-file lineNumber.
  const botGetDisplayNumber = useCallback(
    (i: number, line: ParsedLine) => {
      if (viewMode === 'merge') {
        const topIdx = filteredIndices[i];
        return topIdx !== undefined ? topIdx + 1 : i + 1;
      }
      return line.lineNumber;
    },
    [viewMode, filteredIndices],
  );

  // Allow bookmarking from the filter pane: marks live in top-pane index space,
  // so map the bottom (filtered) row index back to its top index before toggling.
  const handleBotToggleMark = useCallback(
    (botIndex: number) => {
      const topIdx = filteredIndices[botIndex];
      if (topIdx === undefined) return;
      handleToggleMark(topIdx);
    },
    [filteredIndices, handleToggleMark],
  );

  const botRowProps: LineRowProps = useMemo(
    () => ({
      lines: filteredLines, selectedIndex: botSelectedIndex, marks: botMarks, expanded: expandedBot,
      fileColors: fileColorMap, showFile: viewMode === 'merge',
      onSelect: handleBotSelect, onToggleMark: handleBotToggleMark,
      filterRegex,
      getDisplayNumber: botGetDisplayNumber,
    }),
    [filteredLines, botSelectedIndex, botMarks, expandedBot, fileColorMap, viewMode, handleBotSelect, handleBotToggleMark, filterRegex, botGetDisplayNumber],
  );

  const pathBarText = viewMode === 'tabs'
    ? activeFile?.file.displayName ?? ''
    : `Merged from ${files.length} file${files.length === 1 ? '' : 's'}`;
  const pathBarSize = viewMode === 'tabs' ? activeFile?.bytes ?? 0 : totalBytes;
  const pathBarLines = deferredTopLines.length;

  return (
    <Dialog open={open} onClose={onClose} fullScreen disableEscapeKeyDown>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {tooManyFiles && (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              action={<IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>}
            >
              Too many files selected ({files.length}). The klogg viewer accepts at most {KLOGG_MAX_FILES} files.
            </Alert>
          </Box>
        )}

        {!tooManyFiles && (
          <>
            {/* klFiles: file tabs (left) + mode toggles + close (right) */}
            <Box
              sx={{
                display: 'flex', alignItems: 'stretch', height: 38, bgcolor: '#EDEDED',
                borderBottom: '1px solid', borderColor: 'divider', overflow: 'hidden', flexShrink: 0,
              }}
            >
              <Box
                ref={tabStripRef}
                onWheel={(e) => {
                  // Translate vertical wheel into horizontal scroll so mouse users
                  // (and macOS trackpads) can reach tabs past the right edge.
                  if (e.deltaY === 0) return;
                  const el = e.currentTarget;
                  if (el.scrollWidth <= el.clientWidth) return;
                  el.scrollLeft += e.deltaY;
                }}
                sx={{ display: 'flex', alignItems: 'flex-end', flex: 1, overflowX: 'auto', overflowY: 'hidden', px: 0.5 }}
              >
                {fileDataList.map((fd, i) => {
                  const isActive = i === activeTab && viewMode === 'tabs';
                  return (
                    <Box
                      key={i}
                      data-tab-index={i}
                      onClick={() => { setViewMode('tabs'); setActiveTab(i); }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0,
                        px: 1.5, height: isActive ? 36 : 30, mt: isActive ? 0 : '6px',
                        bgcolor: isActive ? '#FFFFFF' : '#E0E0E0',
                        borderTop: '1px solid', borderLeft: '1px solid', borderRight: '1px solid',
                        borderColor: 'divider', borderTopLeftRadius: 6, borderTopRightRadius: 6,
                        cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap',
                        color: isActive ? 'text.primary' : 'text.secondary',
                        fontWeight: isActive ? 600 : 400, mr: '-1px',
                        '&:hover': { bgcolor: isActive ? '#FFFFFF' : '#EAEAEA' },
                      }}
                    >
                      <CircleIcon sx={{ fontSize: 9, color: fd.color }} />
                      <span>{fd.file.displayName}</span>
                      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                        {formatBytes(fd.bytes || 0)}
                      </Typography>
                      {fd.loading && <CircularProgress size={10} />}
                      {fd.error && (
                        <Tooltip title={fd.error}>
                          <Typography sx={{ fontSize: 10, color: 'error.main' }}>err</Typography>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5, gap: 0.75 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={viewMode}
                  onChange={(_, v) => { if (v) setViewMode(v); }}
                >
                  <ToggleButton value="tabs" sx={{ px: 1.25, py: 0.25, fontSize: 11, textTransform: 'none' }}>
                    Tabs
                  </ToggleButton>
                  <Tooltip title={files.length < 2 ? 'Select at least 2 files' : 'Merge by timestamp'}>
                    <span>
                      <ToggleButton
                        value="merge"
                        disabled={files.length < 2}
                        sx={{ px: 1.25, py: 0.25, fontSize: 11, textTransform: 'none' }}
                      >
                        Merge
                      </ToggleButton>
                    </span>
                  </Tooltip>
                </ToggleButtonGroup>
                <Tooltip title="Keyboard shortcuts (?)">
                  <IconButton
                    ref={helpButtonRef}
                    size="small"
                    onClick={() => setHelpAnchor((cur) => (cur ? null : helpButtonRef.current))}
                    sx={{ ml: 0.5 }}
                  >
                    <HelpIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={onClose} sx={{ ml: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {sizeExceeded && (
              <Alert severity="warning" sx={{ borderRadius: 0 }}>
                Total downloaded size exceeded {formatBytes(KLOGG_MAX_TOTAL_BYTES)}. Some files were not loaded.
              </Alert>
            )}

            {/* klTB: path / info bar */}
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, height: 32, px: 1.5, bgcolor: '#FAFAFA',
                borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', flex: 1, height: 24, px: 1,
                  bgcolor: '#FFFFFF', border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  fontFamily: 'monospace', fontSize: 11, color: 'text.secondary',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                /devices/{deviceId}/logs/{pathBarText}
              </Box>
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{formatBytes(pathBarSize)}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>UTF-8</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                Line {selectedIndex !== null ? (selectedIndex + 1).toLocaleString() : '—'} / {pathBarLines.toLocaleString()}
              </Typography>
              {marks.size > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Tooltip title="Previous bookmark">
                    <IconButton size="small" onClick={() => navigateMarks('prev')}>
                      <PrevIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography sx={{ fontSize: 11, color: colors.primary.main, fontWeight: 600 }}>
                    {marks.size}
                  </Typography>
                  <Tooltip title="Next bookmark">
                    <IconButton size="small" onClick={() => navigateMarks('next')}>
                      <NextIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {/* The beta badge lives in the tooltip and the dialog title,
                      not beside this button: the toolbar is 11px and already
                      crowded, and anyone about to share reads the dialog. */}
                  {shareable && (
                    <Tooltip title={`Share these lines with someone. ${BETA_FEATURE_HINT}`}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setShareOpen(true)}
                        sx={{ ml: 0.5, py: 0.15, px: 1, fontSize: 11, textTransform: 'none', minWidth: 0 }}
                      >
                        Share
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>

            {/* klTop + klFBar + klBot */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              {/* klTop — fills remaining vertical space via CSS flex */}
              <Box ref={topPaneRef} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <ColumnHeader showFile={viewMode === 'merge'} />
                {isLoading && deferredTopLines.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2, fontSize: 12 }} color="text.secondary">
                      Loading {fileDataList.filter((fd) => fd.loading).length} of {files.length} files…
                    </Typography>
                  </Box>
                ) : deferredTopLines.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled', flex: 1 }}>
                    <Typography variant="body2">No content to display</Typography>
                  </Box>
                ) : (
                  <List
                    // key forces a fresh List on tab/mode swap so react-window
                    // doesn't reconcile measurements against a wholly different data set.
                    key={viewKey}
                    listRef={topListRef}
                    rowComponent={LineRowRenderer}
                    rowCount={deferredTopLines.length}
                    rowHeight={topRowHeight}
                    rowProps={topRowProps}
                    overscanCount={20}
                    // Fill the flex column instead of a measured pixel height:
                    // react-window v2's List defaults to flexGrow:1 + maxHeight:100%,
                    // so it always matches (pane − 24px header) exactly. The old
                    // `topPaneSize.height - 24` left the list short whenever the
                    // ResizeObserver measurement was stale → white blank below it.
                    style={{ minHeight: 0, opacity: isContentStale ? 0.55 : 1, transition: 'opacity 120ms' }}
                  />
                )}
              </Box>

              {filterRegex && <ResizeHandle onDrag={handleResize} />}

              {/* klFBar: filter bar (always visible) */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75, height: 38, px: 1, bgcolor: '#F4F4F4',
                  borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
                }}
              >
                <Tooltip title="Match case (Aa)">
                  <ToggleButton
                    value="case"
                    selected={caseSensitive}
                    onChange={() => patch((prev) => ({ caseSensitive: !prev.caseSensitive }))}
                    size="small"
                    sx={{
                      border: 1, borderColor: 'divider', px: 0.75, py: 0.25, fontSize: 11, fontWeight: 600,
                      minWidth: 30, bgcolor: '#FFFFFF',
                    }}
                  >
                    Aa
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Regular expression (.*)">
                  <ToggleButton
                    value="regex"
                    selected={isRegex}
                    onChange={() => patch((prev) => ({ isRegex: !prev.isRegex }))}
                    size="small"
                    sx={{
                      border: 1, borderColor: 'divider', px: 0.75, py: 0.25, fontSize: 11, fontWeight: 700,
                      fontFamily: 'monospace', minWidth: 30, bgcolor: '#FFFFFF',
                      color: isRegex ? colors.primary.main : 'text.primary',
                    }}
                  >
                    .*
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Whole word (W)">
                  <ToggleButton
                    value="word"
                    selected={wholeWord}
                    onChange={() => patch((prev) => ({ wholeWord: !prev.wholeWord }))}
                    size="small"
                    sx={{
                      border: 1, borderColor: 'divider', px: 0.75, py: 0.25, fontSize: 11, fontWeight: 600,
                      minWidth: 30, bgcolor: '#FFFFFF',
                    }}
                  >
                    W
                  </ToggleButton>
                </Tooltip>

                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<DropDownIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => setPresetsAnchor(e.currentTarget)}
                  sx={{ fontSize: 11, textTransform: 'none', py: 0.25, bgcolor: '#FFFFFF', whiteSpace: 'nowrap' }}
                >
                  Predefined filters
                </Button>
                <Menu anchorEl={presetsAnchor} open={Boolean(presetsAnchor)} onClose={() => setPresetsAnchor(null)}>
                  {PREDEFINED_FILTERS.map((p) => (
                    <MenuItem
                      key={p}
                      onClick={() => { patch({ filterText: p, isRegex: false }); setPresetsAnchor(null); }}
                      sx={{ fontFamily: 'monospace', fontSize: 12 }}
                    >
                      {p}
                    </MenuItem>
                  ))}
                </Menu>

                <TextField
                  inputRef={filterInputRef}
                  placeholder="Filter — type to highlight + show matches below"
                  size="small"
                  value={filterText}
                  onChange={(e) => patch({ filterText: e.target.value })}
                  sx={{ flex: 1, maxWidth: 520, bgcolor: '#FFFFFF' }}
                  slotProps={{
                    input: {
                      endAdornment: filterText ? (
                        <InputAdornment position="end">
                          <Tooltip title="Clear">
                            <IconButton size="small" onClick={() => patch({ filterText: '' })}>
                              <ClearIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                      sx: { fontSize: 12, fontFamily: 'monospace' },
                    },
                  }}
                />

                <Box sx={{ flex: 1 }} />

                {filterRegex && filteredIndices.length > 0 && (
                  <>
                    <Tooltip title="Previous match">
                      <IconButton size="small" onClick={() => navigateMatch('prev')}>
                        <PrevIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Next match">
                      <IconButton size="small" onClick={() => navigateMatch('next')}>
                        <NextIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                {filterRegex && (
                  <Box
                    sx={{
                      px: 1.25, py: 0.5, borderRadius: 1, bgcolor: colors.primary.main, color: '#FFFFFF',
                      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                    }}
                  >
                    {filteredIndices.length.toLocaleString()} match{filteredIndices.length === 1 ? '' : 'es'} found
                  </Box>
                )}
              </Box>

              {/* klBot: shrinks to content; capped by user's drag preference + max ratio */}
              {filterRegex && (
                <Box sx={{ height: effectiveBotHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
                  <ColumnHeader showFile={viewMode === 'merge'} />
                  {filteredLines.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.disabled', flex: 1 }}>
                      <Typography variant="caption">No lines match the filter</Typography>
                    </Box>
                  ) : (
                    <List
                      key={viewKey}
                      rowComponent={LineRowRenderer}
                      rowCount={filteredLines.length}
                      rowHeight={botRowHeight}
                      rowProps={botRowProps}
                      overscanCount={20}
                      style={{ height: Math.max(effectiveBotHeight - 24, 50), opacity: isContentStale ? 0.55 : 1, transition: 'opacity 120ms' }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </>
        )}

        <LogShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          deviceId={deviceId}
          lines={shareOpen ? buildExcerpt() : []}
          files={shareFiles}
        />

        <Popover
          open={Boolean(helpAnchor)}
          anchorEl={helpAnchor}
          onClose={() => setHelpAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 0.5 } } }}
        >
          <Box sx={{ p: 2, minWidth: 360 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Keyboard shortcuts
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.75 }}>
              {SHORTCUTS.map((s) => (
                <Fragment key={s.keys}>
                  <Box
                    sx={{
                      fontFamily: 'monospace', fontSize: 11, px: 0.75, py: 0.25,
                      bgcolor: '#F4F4F4', border: '1px solid', borderColor: 'divider',
                      borderRadius: 0.5, whiteSpace: 'nowrap', alignSelf: 'center',
                    }}
                  >
                    {s.keys}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', alignSelf: 'center' }}>
                    {s.description}
                  </Typography>
                </Fragment>
              ))}
            </Box>
          </Box>
        </Popover>
      </DialogContent>
    </Dialog>
  );
}
